import { APP_CONFIG } from '@appConfig/_app-config_master';
import { APP_MODE }   from '@appConfig/app-mode-config';

function deepRead( obj: object, path: string ): any {
	if ( !obj || !path ) { return null; }

	const pathParts = path.split( '.' );

	const result = pathParts.reduce(
		( currentValue, currentKey) => ( currentValue && currentValue[ currentKey ] ) ?? null, obj
	);
	return result;
}


/**
 * Sets a (deeply nested) value in an object according to a given path.
 * @param obj The object to be changed
 * @param path The path to the key whose value is to be changed
 * @param value The new value to be set according to the path
 * @param appendIfNotExists Flag that allows to add `value` to the (nested
 *                          sub-)object if the terminal key defined by the path
 *                          does not yet exist in that (sub-)object. This also
 *                          works for higher hierarchy levels, i.e. if `obj` is
 *                          an empty object, it will get all the nested objects
 *                          implied by the path.
 * If no object or no path is provided, the input object is left unchanged.
 */
function deepWrite( obj: object, path: string, value: any, appendIfNotExists?: boolean ): void {
	if ( !obj || !path ) { return }

	const pathParts = path.split( '.' );
	const length = pathParts.length;
	const currentKey = pathParts[ 0 ];

	// object key exists
	if ( currentKey in obj ) {
		const valueAtCurrentKey = obj[ currentKey ];
		// value is object and path points into it
		if ( isObject( valueAtCurrentKey ) && length > 1 ) {
			// calls the function recursively for this nested object
			this.deepWrite(
				valueAtCurrentKey,
				pathParts.splice( 1 ).join( '.' ),
				value,
				appendIfNotExists
			);
		// value is not object, but path points into nested object
		} else if ( !isObject( valueAtCurrentKey ) && length > 1 && appendIfNotExists ) {
			// replaces the primitive value by an empty object and calls the function recursively for it
			obj[ currentKey ] = {};
			this.deepWrite( obj[ currentKey ], pathParts.splice( 1 ).join( '.' ), value, true );
		} else if ( length === 1 ) {
			// overrides value at terminal key
			obj[ currentKey ] = value;
		}
	// append if object key does not exist
	} else if ( appendIfNotExists ) {
		// writes missing key from given path with an empty object assigned and goes to the next deeper path segment
		if ( length > 1 ) {
			obj[ currentKey ] = {};
			this.deepWrite( obj[ currentKey ], pathParts.splice( 1 ).join( '.' ), value, true );
		} else {
			// sets value at terminal key
			obj[ currentKey ] = value;
		}
	}
}


/**
 * IMPORTANT NOTE: This function employs the `JSON` object. The `JSON` object,
 * however, neither supports `Function` objects nor the data type `undefined`!
 * If such entries exist in the source object, they won't be present in the copy
 * at all (if in arrays these values will be converted to `null`)!
 * Also: `Date` objects will be converted to plain ISO date strings, `Set` and
 * `Map` objects will be converted to empty plain objects, and `NaN` will be
 * converted to `null`!
 */
function deepCopy<T>( obj: T ): T {
	return !isNullish( obj ) ? JSON.parse( JSON.stringify( obj ) ) : obj;
}


/**
 * IMPORTANT NOTE: This function copies multidimensional plain JS objects as
 * well as Arrays, Maps and Sets. Also, `Date` objects are supported.
 *
 * Note that `Function` objects will be copied, but this copy and the original
 * `Function` object will share the same reference. This, however, shouldn't
 * cause any problems since functions can't be changed at runtime.
 *
 * CAUTION! If the object to be cloned includes "infinitely" complex nested
 * objects such as instances of `TemplateRef` or certain instances of
 * `HTMLElement` (e.g. DOM nodes of Angular components) then this can cause a
 * stack overflow (`InternalError: too much recursion`)!
 */
function deepCopyByRecursion<T extends object>( input: T ): T {
	if ( !isObject( input ) ) { return input; }

	// typeof input === 'object'
	let copy: T;

	switch ( input.constructor.name ) {

		case 'Date':
			copy = new Date( input as Date ) as T;
			break;

		case 'Map':
			copy = new Map() as T;
			(
				input as Map<any, any>
			).forEach( ( value, key ) =>
				(
					copy as Map<any, any>
				).set( key, deepCopyByRecursion( value ) )
			);
			break;

		case 'Set':
			copy = new Set() as T;
			(
				input as Set<any>
			).forEach( ( element ) =>
				(
					copy as Set<any>
				).add( deepCopyByRecursion( element ) )
			);
			break;

		case 'RegExp':
			const regEx: RegExp = input as RegExp;
			const regExpCopy = new RegExp( regEx.source, regEx.flags );
			regExpCopy.lastIndex = regEx.lastIndex;
			copy = regExpCopy as T;
			break;

		default: // i.e. Arrays and plain JS objects
			copy = Array.isArray( input ) ? [] as T : {} as T;
			Object.keys( input ).forEach( key =>
				copy[ key ] = deepCopyByRecursion( input[ key ] )
			);
	}
	return copy;
}


/**
 * Checks if the passed-in value is either nullish or an empty array or
 * plain object ("POJO"). Note that the function will return `false` for *any*
 * primitive (even those which may be considered "empty" such as an empty
 * string), and `true` for *any* other object than Array or "POJO", regardless
 * of it holding values or not.
 */
function isEmpty( object ): boolean {
	return isNullish( object )
				 ? true
				 : isObject( object ) && Object.keys( object ).length === 0;
}


function isNullish( object ): boolean {
	return object === undefined || object === null;
}


/**
 * Checks if the value passed in as argument is an object.
 * NOTE: In its current state, the function will return `false` for Function
 * objects!
 */
function isObject( object ): boolean {
	return object !== null && typeof object === 'object';
}


/**
 * Collects the paths to all properties of a complex plain JS object ("POJO") as
 * strings into an array.
 * @param object The object the paths of which are to be collected.
 * @param prefix A string for prefixing the first element of each collected path.
 * @returns An array of strings containing all collected paths.
 * Limitations: From the path collection excluded are array indices and `Map`
 * keys as well as properties of POJOs nested inside arrays and `Map`s. So the
 * key of property holding another object type than POJO will be the terminal
 * element of a collected path.
 */
function recursivelyGetAllObjectsKeyPaths( object: object, prefix?: string ): string[] {
	prefix = prefix ?? '';
	const hasExcludedType = ( obj: object ) => [ 'Array',	'Date', 'Map', 'RegExp', 'Set' ].includes( obj.constructor.name );
	object = isNullish( object ) || hasExcludedType( object ) ? {} : object;
	return Object.keys( object ).reduce( ( res: string[], key: string ): string[] => {
		if ( isObject( object[ key ] ) && !hasExcludedType( object[ key ] ) ) {
			return [ ...res, ...recursivelyGetAllObjectsKeyPaths( object[ key ], prefix + key + '.' ) ];
		}
		return [ ...res, prefix + key ];
	}, [] );
}


/**
 * Searches properties of plain JS objects (POJOs) and elements of arrays
 * recursively for empty strings and replaces them by `null`.
 *
 * @param inputObject - the object to be sanitized
 * @returns a sanitized **deep copy** of the input object
 * NOTE: The function does not support sanitization of other data container
 * types such as `Map` and `Set` yet, but is able to include such objects
 * unchanged in the return value. *
 */
function sanitizeEmptyStringsToNull( inputObject: object ): object {
	/*
	 * Local, conditionally recursively called function, meant to hide the
	 * `isRecursion` flag to the outside, which in turn is needed to prevent
	 *  creation of deep copies on each recursive call.
	 */
	const sanitize = ( obj: object, isRecursion?: boolean ): object => {
		if ( !isEmpty( obj ) ) {
			// create deep copy on first call only
			obj = isRecursion ? obj : deepCopyByRecursion( obj );

			Object.keys( obj ).forEach( ( key: string ) => {
				if ( isObject( obj[ key ] ) ) {
					obj[ key ] = sanitize( obj[ key ], true );
				}
				else if ( obj[ key ] === '' ) { obj[ key ] = null }
			} );
		}
		return obj
	}
	return sanitize( inputObject );
}


/**
 * Checks first object references and second stringifies both objects and
 * does a simple string comparison for equality.
 * @param objectA A plain JS object ("POJO") for comparison
 * @param objectB The plain JS object ("POJO") to be compared with `objectA`
 * @returns `true` if references are same or keys and values including orders of
 *          both are exactly the same, otherwise `false`.<br>
 * Limitations and peculiarities: The function...
 * * ...cannot handle special object types such as `Set`, `Map` or `RegExp`
 *   (will always return `true` then, even for different types!), but can handle
 *   `Date`s.
 * * ...will consider `null` and `undefined` not equal when values of properties
 *   of plain objects, but equal when values of array elements!<br>
 * Note: This function is mainly used in form services to check whether the
 *       values on the fields of the FormGroup have actually changed.
 */
function simpleIsEqual( objectA: any, objectB: any ): boolean {
	if ( objectA === objectB ) { return true; }
	return JSON.stringify( objectA ) === JSON.stringify( objectB );
}


/**
 * Recursively checks two arrays or plain objects ("POJOs") for equality.
 * @param object1 An array or plain JS object ("POJO") for comparison
 * @param object2 The array or plain JS object ("POJO") to be compared with `object1`
 *
 * Notes:
 * * Both `undefined` and `null` are always considered equal!
 * * The function does not support any type of specific JS object such as`Date`,
 *   `Map` or `Set`. If two such objects are compared, the function will always
 *   return `true` even if not equal by content, and even if different type!
 */
function simpleIsEqualByRecursion( object1: object, object2: object ): boolean {
	// "not equal" if only one of the inputs is nullish
	if ( !object1 && !!object2 || !!object1 && !object2 ) { return false; }
	// "equal" if both are either nullish or share the same reference
	else if ( !object1 && !object2 || object1 === object2 ) { return true; }

	/*
	 * Will result in empty arrays for object types Date, Set, Map and probably
	 * more. This would lead to the comparison logic in the loop below be
	 * completely skipped, making the function return `true`, even if the Dates,
	 * Sets, Maps etc. aren't equal.
	 */
	const keys1 = Object.keys( object1 );
	const keys2 = Object.keys( object2 );

	if ( keys1.length !== keys2.length ) {
		return false;
	}

	for ( const key of keys1 ) {
		const value1 = object1[ key ];
		const value2 = object2[ key ];
		const areBothObjects = isObject( value1 ) && isObject( value2 );
		if (
			areBothObjects && !simpleIsEqualByRecursion( value1, value2 ) ||
			!areBothObjects && !isNullish( value1 ) && !isNullish( value2 ) && value1 !== value2
		) {
			return false;
		}
	}

	return true;
}


type SortOrderType = 'asc' | 'desc' | 'skip';
type SortConfigItemType<T> = { key: keyof T, order: SortOrderType };

/**
 * Sorts an array of (flat) plain objects by primitive value of one or more of
 * its properties.
 * @param array - The array of objects.
 * @param sortConfig - An object that contains the key of the property by which
 * the array is to be sorted, and the sort order (asc/desc/skip),
 * e.g. `{ key: 'name', order: 'asc' }`. Note that
 * * `order: 'skip'` will make the property "sort-ignored", except that
 *   `undefined` will always cause the object to be placed at the last possible
 *    position.
 * * `order: null`, however, will behave exactly as `order: asc`.
 * * `key: null` will have no effect on the sorting behavior whatsoever.
 * * if multiple config objects are provided for the same property, then the
 *   first will determine the sorting priority, and the last will determine the
 *   sort direction. For example, the sort config
 *   `[ { key: 'a', order: 'asc' }, { key: 'b', order: 'asc' }, { key: 'a', order: 'desc' }, ]`
 *   will cause the array to be primarily sorted by property `a`, but the sort
 *   order for that property to be descending.
 * @returns The sorted array. Note that in strings the sorting is
 * "case-sensitive", i.e. 'Z' is considered a lexicographic predecessor of 'a'.
 */
function sortByObjectValues<T extends object>( array: T[], sortConfig: SortConfigItemType<T>[] = [] ): T[] {
	// Return unsorted array if no sort object is supplied.
	if ( isEmpty( sortConfig ) ) {
		return array;
	}

	const isValidSortConfigItem = ( sortConfigItem: SortConfigItemType<T> ) => {
		if ( isNullish( sortConfigItem.key ) ) {
			if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) { // log only in local dev environment
				console.warn( 'ObjectUtils:sortByObjectValues -> sort config key nullish, removed corresponding item from sort config array' );
			}
			return false;
		}
		return true;
	}
	sortConfig = sortConfig.filter( isValidSortConfigItem )

	const sortKeys = sortConfig
	.map( sortConfigItem => sortConfigItem.key as string );

	// map sort order strings to numbers for usage in the Array.prototype.sort() callback
	const sortOrderMap: { [ key in keyof T ]?: number } = {};
	sortConfig.forEach( sortConfigItem => {
		const key: keyof T = sortConfigItem.key;
		const sortOrderTypeToNumberMap: { [ key in SortOrderType ]?: number } = {
			[ 'desc' ]: -1,
			[ 'skip' ]: 0
		};

		sortOrderMap[ key ] = sortOrderTypeToNumberMap[ sortConfigItem.order ] ?? 1;	// default sort order is ascending
	} );

	const keySort = (a: T, b: T, sortOrder: number): number => {
		if ( a === b ) { // If the values are the same, do not switch positions.
			return 0;
		}

		/*
		 * If one of the compared values is explicitly set to `undefined`, always
		 * treat it as the greater value (needed because every comparison operation
		 * with 1 operand being `undefined` returns `false`, leading to inconsistent
		 * sorting behavior).
		 */
		if ( a === undefined ) {
			return 1;
		}
		if ( b === undefined ) {
			return -1;
		}

		// If b > a, multiply by -1 to reverse switch direction.
		return a > b ? sortOrder : -1 * sortOrder;
	};

	return array?.toSorted( ( a: T, b: T ) => {
		let sorted = 0;
		let index = 0;

		// Loop until sorted (-1 or 1) or until all sort keys have been processed.
		while ( sorted === 0 && index < sortKeys.length ) {
			const key = sortKeys[ index ];

			if ( key ) {
				const sortOrder = sortOrderMap[ key ];

				sorted = keySort( a[ key ], b[ key ], sortOrder );
				index++;
			}
		}

		return sorted;
	} );
}


type itemMap = ( n: any ) => any;

interface SortConfig<T> {
	key: keyof T;
	reverse?: boolean;
	map?: itemMap;
}

/**
 * Allows getting an array of (flat) plain objects sorted by the primitive
 * values of one or more of its properties.
 * @param keys An array which either is a list of the keys of the properties by
 * which the array is to be sorted, or a list of config objects which can
 * include the following parameters:
 * @param keys.key Key of the property by which the array is to be sorted.
 * @param keys.reverse Inverts the sort order for this property if set and `true`.
 * @param keys.map Holds a map for property value conversion. Note that the
 *        conversion merely is part of the sorting process, i.e., the original
 *        values in the object remain unchanged.
 * @returns A sort function that can be used as callback of the
 * `Array.prototype.sort()` function
 */
function sortByObjectValuesCompareFnGenerator<T extends object>( keys: ( ( keyof T ) | SortConfig<T> )[] ): ( a: T, b: T ) => 0 | 1 | -1 {
	if ( !keys?.length ) { return () => 0; }

	return ( a: T, b: T ) => {
		const firstKey: keyof T | SortConfig<T> = keys[ 0 ];

		if ( isNullish( firstKey ) ) { return 0; }

		// set sort config parameters (if there are any)
		const sortByConfig = typeof firstKey !== 'string';
		const key: keyof T = sortByConfig
												 ? ( firstKey as SortConfig<T> ).key
												 : firstKey as keyof T;
		const reverse: boolean = sortByConfig
														 ? !!( firstKey as SortConfig<T> ).reverse
														 : false;
		const map: itemMap = sortByConfig
												 ? ( firstKey as SortConfig<T> ).map ?? null
												 : null;

		const valA = map ? map( a[ key ] ) : a[ key ];
		const valB = map ? map( b[ key ] ) : b[ key ];

		/*
		 * If one of the compared values is (explicitly set to) `undefined`, always
		 * treat it as the greater value (needed because every comparison operation
		 * with 1 operand being `undefined` returns `false`, leading to inconsistent
		 * sorting behavior).
		 */
		if ( valA === undefined ) {
			return 1;
		}
		if ( valB === undefined ) {
			return -1;
		}

		if ( valA === valB ) {
			if ( keys.length === 1 ) {
				return 0;
			}
			return sortByObjectValuesCompareFnGenerator<T>( keys.slice( 1 ) )( a, b );
		}
		if ( reverse ) {
			return valA > valB ? -1 : 1;
		}
		return valA > valB ? 1 : -1;
	};
}


export {
	deepCopy,
	deepCopyByRecursion,
	deepRead,
	deepWrite,
	isEmpty,
	isNullish,
	isObject,
	recursivelyGetAllObjectsKeyPaths,
	sanitizeEmptyStringsToNull,
	simpleIsEqualByRecursion,
	simpleIsEqual,
	sortByObjectValues,
	sortByObjectValuesCompareFnGenerator
};
